import { Group, MeshStandardMaterial, type Mesh } from "three";
import { BOSS_IDS, CREATURE_BY_ID, CREATURE_SCALE, creatureTone, ELITE_SCALE, TONE_COLORS, toneRoles } from "@eldrun/content";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { prepareTree, sharedCharacterMaterial } from "../render/character-surface/apply.ts";
import { actorStyle, creatureRole } from "../render/character-surface/config.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { CREATURE_SHAPES, type CreatureParts, type Paint } from "./creature-shapes.ts";
import { unindexTree } from "../render/geometry/unindexed.ts";

/** Partes articuladas de uma criatura (pivôs nos quadris, ombros e pescoço), na raiz. */
export interface CreatureRig extends Required<CreatureParts> {
  type: string;
  root: Group;
}

export const CREATURE_MODEL_IDS = Object.keys(CREATURE_SHAPES);

/** Escala do boneco: a de todas as criaturas e, na reforçada (não no chefe), a dela por cima (doc 01, Ondas). */
export function creatureScale(type: string, elite: boolean): number {
  return CREATURE_SCALE * (elite && !BOSS_IDS.includes(type) ? ELITE_SCALE : 1);
}

/**
 * Boneco de uma criatura. Sem sombra própria: a sombra-blob no chão (F51) vem da `CreatureLayer`,
 * que não a dá à cópia do Guardião Rosa (é assim que se acha a verdadeira; antes, um disco escuro).
 */
export function createCreatureModel(atmosphere: AtmosphereUniforms, type: string, elite = false): CreatureRig {
  const build = CREATURE_SHAPES[type] ?? CREATURE_SHAPES["capivara"]!;
  // A reforçada brilha no contorno (rim ×3, teto de 3): lê de longe, inclusive à noite. Personagem:
  // na máscara do contorno; as assombrações ganham o rim frio da noite (F51).
  const archetype = CREATURE_BY_ID.get(type)?.archetype ?? "neutra";
  const look = { outline: true, nightRim: archetype === "assombracao" };
  // Malha neon (F60): o tom do arquétipo (chefe vermelho, monstro laranja, fauna neutra).
  const tone = creatureTone(archetype);
  const accent = TONE_COLORS[tone];
  const roles = toneRoles(tone);
  // Superfície dos atores (F119, ADR 0025): o corpo no PBR com a cor da peça, linhas e nós no tom, um
  // material dividido por cor e visual (o lote continua um desenho por combinação). A peça acesa (olhos,
  // fogo) é o núcleo neon, fonte de luz: fica no toon.
  const style = actorStyle(creatureRole(tone), accent);
  const paint: Paint = (hex, rim = 1, glow = false) => {
    const r = elite ? Math.min(3, rim * 3) : rim;
    return glow ? toonMaterial(atmosphere, hex, r, true, { ...look, role: roles.core, accent }) : sharedCharacterMaterial(atmosphere, { color: hex, style, look: { rim: r, nightRim: look.nightRim } });
  };
  const parts = build(paint);
  const root = new Group();
  root.add(parts.body);
  // As primitivas sem índice e preparadas de vez (as geometrias das formas são divididas entre os bonecos).
  unindexTree(root);
  prepareTree(root, { include: (mesh: Mesh) => mesh.material instanceof MeshStandardMaterial });
  // Chefe reforçado (Pedra) só brilha: o corpo no servidor tem o mesmo tamanho.
  root.scale.setScalar(creatureScale(type, elite));
  return { type, root, arms: [], wings: [], ...parts };
}
